import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCustomer } from "../lib/CustomerContext";
import { formatApiErrorDetail } from "../lib/api";
import SEO from "../lib/SEO";

export default function Signup() {
    const { register } = useCustomer();
    const navigate = useNavigate();
    const location = useLocation();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [submitting, setSubmitting] = useState(false);

    const redirect = new URLSearchParams(location.search).get("next") || "/account";

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        setSubmitting(true);
        try {
            const result = await register(form.name, form.email, form.password);
            toast.success(`Welcome, ${result.customer.name.split(" ")[0]}!`);
            if (result.linked_previous_orders > 0) {
                toast.info(`We linked ${result.linked_previous_orders} of your past order(s) to this account.`);
            }
            navigate(redirect);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="signup-page" className="pt-24 md:pt-[10rem] pb-20 bg-[#FFF7FB] min-h-[70vh]">
            <SEO title="Sign up — Cake Central" path="/signup" />
            <div className="max-w-md mx-auto px-4 sm:px-6">
                <h1 className="font-display text-4xl md:text-5xl text-ink mb-2 leading-none">Create an account</h1>
                <p className="text-sm text-ink-soft mb-8">
                    Track your orders, save your details, and skip the form next time.
                </p>

                <form onSubmit={onSubmit} className="bg-white border border-[#EAE3DE] p-6 space-y-4">
                    <div>
                        <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Full Name</label>
                        <input
                            type="text"
                            required
                            autoFocus
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                            data-testid="signup-name"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Email</label>
                        <input
                            type="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                            data-testid="signup-email"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Password (min 8 characters)</label>
                        <input
                            type="password"
                            required
                            minLength={8}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                            data-testid="signup-password"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-pink w-full justify-center disabled:opacity-60"
                        data-testid="signup-submit"
                    >
                        {submitting ? "Creating your account…" : "Create account"}
                    </button>
                    <p className="text-[11px] text-ink-soft text-center">
                        If you've ordered before with this email, we'll link those orders to your new account automatically.
                    </p>
                </form>

                <p className="text-sm text-ink-soft text-center mt-6">
                    Already have an account?{" "}
                    <Link to="/login" className="text-blush hover:underline">Log in</Link>
                </p>
            </div>
        </main>
    );
}
